import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Mail } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import ContourBackground from "@/components/ContourBackground";
import Reveal from "@/components/Reveal";
import { getStateBySlug, getCountyInState } from "@/data/usLocations";
import { SERVICES } from "@/data/services";
import { CAD_SERVICES } from "@/data/cadServices";
import {
  SURVEY_KEYWORDS,
  CAD_KEYWORDS,
  PRIMARY_KEYWORDS,
  composeKeywords,
} from "@/data/seoKeywords";
import { countyLd, breadcrumb } from "@/lib/jsonLd";

export default function CountyPage() {
  const { stateSlug, countySlug } = useParams();
  const state = getStateBySlug(stateSlug);
  const county = getCountyInState(state, countySlug);
  const valid = !!state && !!county;

  useSeo({
    title: valid
      ? `CAD Drafting Services — ${county.name}, ${state.abbr} · Tri-Axis XYZ`
      : "Coverage — Tri-Axis XYZ",
    description: valid
      ? `Outsource land-survey CAD, ALTA, boundary, topo, plats, MEP/BIM, structural and civil drafting in ${county.name}, ${state.state}. Serving ${county.seat} and ${county.zips.length} ZIP codes.`
      : "",
  });

  useJsonLd(
    valid ? countyLd(state, county) : null,
    `county-${stateSlug}-${countySlug}`,
  );
  useJsonLd(
    valid
      ? breadcrumb([
          { name: "Home", path: "/" },
          { name: "Coverage", path: "/cad-services" },
          { name: state.state, path: `/cad-services/${state.slug}` },
          { name: county.name, path: `/cad-services/${state.slug}/${county.slug}` },
        ])
      : null,
    `county-bc-${stateSlug}-${countySlug}`,
  );

  if (!state) return <Navigate to="/cad-services" replace />;
  if (!county) return <Navigate to={`/cad-services/${state.slug}`} replace />;

  const nearby = state.counties.filter((c) => c.slug !== county.slug).slice(0, 6);
  const contactHref = `/contact?country=USA&state=${encodeURIComponent(
    `${county.name}, ${state.state}`,
  )}&code=${state.abbr}`;

  return (
    <div data-testid="page-county">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.5} seed={county.slug.length + 7} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <nav className="mono text-[10px] tracking-[0.22em] uppercase text-topo flex items-center gap-2 flex-wrap">
              <Link to="/cad-services" className="hover:text-emerald-700">
                Coverage
              </Link>
              <span>›</span>
              <Link
                to={`/cad-services/${state.slug}`}
                className="hover:text-emerald-700"
              >
                {state.state}
              </Link>
              <span>›</span>
              <span className="text-slate-900">{county.name}</span>
            </nav>
            <h1
              data-testid="county-page-title"
              className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.04]"
            >
              CAD drafting & land-survey CAD —{" "}
              <span className="text-emerald-700">
                {county.name}, {state.abbr}
              </span>
            </h1>
            <p className="mt-5 max-w-3xl text-base sm:text-lg text-slate-600 leading-relaxed">
              We support surveying firms, engineering houses, architects and
              builders across <strong>{county.name}</strong> ({county.seat}) with
              Civil 3D, AutoCAD, MicroStation, Revit MEP and ArcGIS production.
              ALTA / boundary / topo / plats / ROW / MEP-BIM / structural —
              stamped-ready, on schedule.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={contactHref}
                data-testid="county-cta-quote"
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-3"
              >
                Get a {county.name} quote <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="mailto:contact@triaxisxyz.com"
                className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-3 bg-white"
              >
                <Mail className="h-4 w-4" /> Email us directly
              </a>
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="border border-slate-300 bg-white p-5">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                County snapshot
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-slate-500">County seat</dt>
                  <dd className="font-bold text-slate-900">{county.seat}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">State</dt>
                  <dd className="font-bold text-slate-900">
                    {state.state} ({state.abbr})
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Region</dt>
                  <dd className="font-bold text-slate-900">{state.region}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">ZIPs we cover</dt>
                  <dd className="font-bold text-slate-900">{county.zips.length}+</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </section>

      {/* ZIP grid */}
      <section className="bg-white border-b border-slate-200 py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              ZIP coverage · {county.name}
            </div>
            <h2 className="display mt-2 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
              {county.zips.length} primary ZIP codes
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Field crews submit drawings from anywhere — we draft, QC and
              deliver into your CAD standards. Sample ZIPs we actively service
              in this county:
            </p>
          </Reveal>
          <ul
            data-testid="county-zip-grid"
            className="mt-6 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2"
          >
            {county.zips.map((z) => (
              <li
                key={z}
                className="mono text-[12px] font-bold tracking-wide text-slate-900 bg-paper-dark/40 border border-slate-300 px-2 py-2 text-center"
              >
                {z}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Services in this county */}
      <section className="bg-paper-dark/30 border-b border-slate-200 py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              🧭 Land-Survey CAD · {county.name}
            </div>
            <h3 className="display mt-2 text-2xl font-extrabold tracking-tight text-slate-900">
              ALTA · Boundary · Topo · Plat · ROW · GIS
            </h3>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-slate-700 hover:text-emerald-700"
                  >
                    {s.emoji} {s.name}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              {composeKeywords(SURVEY_KEYWORDS).slice(0, 500)}…
            </p>
          </Reveal>
          <Reveal>
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
              📐 CAD Beyond Survey · {county.name}
            </div>
            <h3 className="display mt-2 text-2xl font-extrabold tracking-tight text-slate-900">
              Architectural · Structural · MEP · Mechanical · Civil
            </h3>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {CAD_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/cad-techies#cad-${s.slug}`}
                    className="text-slate-700 hover:text-emerald-700"
                  >
                    {s.emoji} {s.name}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              {composeKeywords(CAD_KEYWORDS, PRIMARY_KEYWORDS).slice(0, 500)}…
            </p>
          </Reveal>
        </div>
      </section>

      {/* Nearby counties */}
      {nearby.length > 0 && (
        <section className="bg-white border-b border-slate-200 py-12">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">
                Nearby in {state.state}
              </div>
              <h2 className="display mt-2 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
                Adjacent counties we also serve
              </h2>
            </Reveal>
            <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {nearby.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/cad-services/${state.slug}/${c.slug}`}
                    data-testid={`nearby-county-${c.slug}`}
                    className="group flex items-center justify-between border border-slate-200 bg-white hover:border-emerald-600 hover:bg-emerald-50 px-3 py-2.5 text-xs font-semibold text-slate-800"
                  >
                    <span className="truncate">
                      <MapPin className="inline h-3 w-3 mr-1 -mt-0.5 text-slate-500 group-hover:text-emerald-700" />
                      {c.name}
                    </span>
                    <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all text-emerald-600" />
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Link
                to={`/cad-services/${state.slug}`}
                className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
              >
                See all {state.counties.length} {state.state} counties{" "}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
